import DataTable from "datatables.net-react";
import DT from "datatables.net-dt";
import "datatables.net-responsive-dt";
import "datatables.net-select-dt";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  deleteContract,
  getAllContracts,
  searchContracts,
} from "../../../redux/contractSlice/contractSlice";
import Logo from "../../../Images/logo192.png";
import Swal from "sweetalert2";
import "./Contracts.css";

DataTable.use(DT);

export default function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [tableKey, setTableKey] = useState(0); // جديد - لإعادة render الجدول
  const dispatch = useDispatch();
  const { allContracts, searchResults, status, searchStatus } = useSelector(
    (state) => state.contract
  );
  const table = useRef();
  const [loading, setLoading] = useState(true);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((keyword) => {
      if (keyword.trim() !== "") {
        setIsSearching(true);
        setLoading(true);
        dispatch(searchContracts({ keyword: keyword.trim(), page: 1 }));
        setCurrentPage(1);
      } else {
        setIsSearching(false);
        setLoading(true);
        dispatch(getAllContracts(1));
        setCurrentPage(1);
      }
    }, 500),
    [dispatch]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    setCurrentPage(1);
    setLoading(true);
    dispatch(getAllContracts(1));
  };

  useEffect(() => {
    if (!isSearching) {
      dispatch(getAllContracts(currentPage));
    }
  }, [dispatch, currentPage, isSearching]);

  // تحديد المصدر الصحيح للبيانات
  const dataSource = isSearching ? searchResults : allContracts;
  const contractsData = dataSource?.data || [];
  const pagination = dataSource?.pagination;

  useEffect(() => {
    if (contractsData) {
      setContracts(contractsData);
      setTableKey((prev) => prev + 1); // إعادة render الجدول
      setLoading(false);
    }
  }, [contractsData]);

  useEffect(() => {
    let mounted = true;

    if (!loading && contracts.length > 0) {
      const handleClick = (e) => {
        const btn = e.target.closest(".delete-btn");
        if (btn) {
          const id = btn.getAttribute("data-id");
          if (id && mounted) {
            handleDelete(id);
          }
        }
      };

      document.addEventListener("click", handleClick);

      return () => {
        mounted = false;
        document.removeEventListener("click", handleClick);
      };
    }
  }, [loading, contracts]);

  const handleDelete = async (contractId) => {
    const result = await Swal.fire({
      title: "هل أنت متأكد؟",
      text: "لن تتمكن من استرجاع هذا العقد بعد الحذف!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "نعم، احذف!",
      cancelButtonText: "إلغاء",
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteContract(contractId)).unwrap();
        if (isSearching) {
          await dispatch(
            searchContracts({ keyword: searchQuery.trim(), page: currentPage })
          );
        } else {
          await dispatch(getAllContracts(currentPage));
        }
        await Swal.fire("تم الحذف!", "تم حذف العقد بنجاح.", "success");
      } catch (error) {
        Swal.fire("خطأ!", "حدث خطأ أثناء حذف العقد، حاول مرة أخرى.", "error");
        console.error("Error deleting contract:", error);
      }
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setLoading(true);
    if (isSearching) {
      dispatch(searchContracts({ keyword: searchQuery.trim(), page }));
    }
  };

  const isLoading =
    status === "loading" || searchStatus === "loading" || loading;
  const totalPages = pagination
    ? Math.ceil(pagination.total / pagination.limit)
    : 1;
  const showPagination = pagination && totalPages > 1;

  const screenColumns = [
    {
      title: "رقم العقد",
      data: "contract_number",
    },
    {
      title: "الشركة",
      data: "branch",
      render: (data) => data?.company?.name || "-",
    },
    {
      title: "الفرع",
      data: "branch",
      render: (data) => data?.name || "-",
    },
    {
      title: "تاريخ البدء",
      data: "start_date",
      render: (data) => format(new Date(data), "yyyy-MM-dd"),
    },
    {
      title: "تاريخ الانتهاء",
      data: "end_date",
      render: (data) => format(new Date(data), "yyyy-MM-dd"),
    },
    { title: "القيمة", data: "value" },
    {
      title: "الحالة",
      data: "status",
      render: (data) => {
        const statusMap = {
          pending: "قيد الانتظار",
          active: "نشط",
          completed: "مكتمل",
          cancelled: "ملغي",
        };
        return statusMap[data] || data;
      },
    },
    { title: "عدد زيارات الصيانة", data: "maintenance_visits" },
    {
      title: "عمليات",
      data: null,
      render: (data, type, row) =>
        `<div class="d-flex gap-3 justify-content-center align-items-center">
          <a href="/Contracts/Contract/${row.id}" class="text-decoration-none" title="عرض التفاصيل">
            <i class="fa-regular fa-eye"></i>
          </a>
          <a href="/Contracts/${row.id}/MaintenanceSchedules" class="text-decoration-none" title="جداول الصيانة">
            <i class="fa-solid fa-table"></i>
          </a>
          <a href="/contracts/addMangerAndStuff/${row.id}" class="text-decoration-none" title="توكيل موظفين">
            <i class="fa-solid fa-users"></i>
          </a>
          <a href="/Contracts/${row.id}/AddStandardMaintenanceItem" class="text-decoration-none" title="إضافة عناصر">
            <i class="fa-solid fa-plus"></i>
          </a>
          <a href="/editContract/${row.id}" class="text-decoration-none" title="تعديل">
            <i class="fa-solid fa-pen"></i>
          </a>
          <button class="delete-btn border-0 bg-transparent" data-id="${row.id}" title="حذف">
            <i class="fa-solid fa-trash text-danger"></i>
          </button>
        </div>`,
    },
  ];

  const printColumns = [
    { title: "رقم العقد", data: "contract_number" },
    {
      title: "الشركة",
      data: "branch",
      render: (data) => data?.company?.name || "-",
    },
    { title: "الفرع", data: "branch", render: (data) => data?.name || "-" },
    {
      title: "تاريخ البدء",
      data: "start_date",
      render: (data) => format(new Date(data), "yyyy-MM-dd"),
    },
    {
      title: "تاريخ الانتهاء",
      data: "end_date",
      render: (data) => format(new Date(data), "yyyy-MM-dd"),
    },
    { title: "القيمة", data: "value" },
    {
      title: "الحالة",
      data: "status",
      render: (data) => {
        const statusMap = {
          pending: "قيد الانتظار",
          active: "نشط",
          completed: "مكتمل",
          cancelled: "ملغي",
        };
        return statusMap[data] || data;
      },
    },
    { title: "عدد زيارات الصيانة", data: "maintenance_visits" },
  ];

  return (
    <>
      <div className="main">
        <div className="container-fluid">
          <div className="row justify-content-between align-items-center mt-3 mb-4">
            {/* عدد العقود */}
            <div className="col-md-3 d-flex justify-content-md-start justify-content-center mb-3">
              <div className="card py-2 px-3">
                <p className="mb-0 primary-100">
                  عدد العقود:{" "}
                  <span className="primary-100">
                    {pagination?.total || contracts.length}
                  </span>
                </p>
              </div>
            </div>

            {/* Search Box */}
            <div className="col-md-5 mb-3">
              <div className="search-box-container-contracts position-relative">
                <input
                  type="text"
                  placeholder="ابحث برقم العقد، الشركة، الفرع..."
                  className="form-control search-input-contracts"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <i className="fas fa-search search-icon-contracts"></i>
                {searchQuery && (
                  <button
                    className="btn-clear-search-contracts"
                    onClick={clearSearch}
                    title="مسح البحث"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              {isSearching && (
                <div className="search-info-contracts text-center mt-2">
                  <small className="text-muted">
                    <i className="fas fa-search me-1"></i>
                    البحث عن: "{searchQuery}"
                  </small>
                </div>
              )}
            </div>

            {/* زر إضافة عقد */}
            <div className="col-md-3 text-md-end text-center mb-3">
              <Link to="/addContract" className="btn btn-primary text-white">
                <i className="fas fa-plus me-2"></i>
                إضافة عقد
              </Link>
            </div>
          </div>

          {/* Loading Spinner */}
          {isLoading ? (
            <div className="loading-spinner-contracts">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="table-responsive" style={{ overflowX: "auto" }}>
                {contracts && contracts.length > 0 ? (
                  <DataTable
                    key={tableKey} // إعادة render الجدول عند تغيير البيانات
                    data={contracts}
                    columns={screenColumns}
                    className="display table table-striped table-bordered"
                    options={{
                      responsive: true,
                      searching: false,
                      paging: false,
                      info: false,
                      destroy: true, // السماح بإعادة إنشاء الجدول
                    }}
                    ref={table}
                  />
                ) : (
                  <div className="text-center py-5">
                    <i className="fas fa-file-contract fa-3x text-muted mb-3"></i>
                    <p className="text-muted">
                      {searchQuery ? "لا توجد نتائج للبحث" : "لا توجد عقود"}
                    </p>
                    {searchQuery && (
                      <button
                        className="btn btn-sm btn-primary mt-2"
                        onClick={clearSearch}
                      >
                        <i className="fas fa-times me-2"></i>
                        مسح البحث
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {showPagination && (
                <div className="pagination-section-contracts mt-4">
                  <nav>
                    <ul className="pagination justify-content-center">
                      <li
                        className={`page-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          السابق
                        </button>
                      </li>

                      {[...Array(totalPages)].map((_, index) => (
                        <li
                          key={index + 1}
                          className={`page-item ${
                            currentPage === index + 1 ? "active" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}

                      <li
                        className={`page-item ${
                          currentPage === totalPages ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          التالي
                        </button>
                      </li>
                    </ul>
                  </nav>

                  <div className="pagination-info-contracts text-center mt-2">
                    <small className="text-muted">
                      الصفحة {currentPage} من {totalPages}
                    </small>
                  </div>
                </div>
              )}

              {/* Results Summary */}
              {contracts.length > 0 && (
                <div className="results-summary-contracts text-center mt-3">
                  <p className="text-muted">
                    عرض {contracts.length} من{" "}
                    {pagination?.total || contracts.length} عقد
                    {searchQuery && (
                      <span className="search-indicator-contracts">
                        {" "}
                        - نتائج البحث عن: "{searchQuery}"
                      </span>
                    )}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Print Section - باقي كما هو */}
      <div className="print-only">
        <div className="header text-center py-3">
          <div className="logo d-flex mb-3">
            <figure className="me-3 mb-0">
              <img src={Logo} alt="logo" />
            </figure>
            <div>
              <h1 className="h5 mb-0 text-start">
                بصمة خبرة لأدوات الأمن والسلامة
              </h1>
              <h1 className="h5 mb-0 text-start">
                Experience Mark For Safety & Security Tools
              </h1>
              <p className="mb-0" dir="ltr">
                Supply, Installation & Maintenance توريد وتركيب وصيانة
              </p>
            </div>
          </div>
          <h1 className="text-dark">قسم الخدمة - صيانة روتينية</h1>
          <p className="text-muted">
            Experience Mark For Safety & Security Tools
          </p>
        </div>

        <div className="table-responsive d-flex justify-content-center align-items-center">
          <div className="table-container">
            {contracts && contracts.length > 0 && (
              <table
                className="display table table-bordered"
                style={{ width: "100%", border: "1px solid #000" }}
              >
                <thead>
                  <tr>
                    {printColumns.map((col, index) => (
                      <th
                        key={index}
                        style={{
                          border: "1px solid #000",
                          padding: "8px",
                          textAlign: "center",
                        }}
                      >
                        {col.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((contract, index) => (
                    <tr key={index}>
                      {printColumns.map((col, colIndex) => (
                        <td
                          key={colIndex}
                          style={{
                            border: "1px solid #000",
                            padding: "8px",
                            textAlign: "center",
                          }}
                        >
                          {col.render
                            ? col.render(contract[col.data], null, contract)
                            : contract[col.data] || ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="footer py-3 text-center">
          <div className="row">
            <div className="col-6">
              <p>اسم العميل: ____________________</p>
              <p>توقيع العميل: ____________________</p>
            </div>
            <div className="col-6">
              <p>اسم التقني: ____________________</p>
              <p>توقيع التقني: ____________________</p>
            </div>
          </div>
          <p>التاريخ: {format(new Date(), "yyyy-MM-dd")}</p>
          <p>هذا ليس بوصل دفع. يرجى طلب الوصل عند الدفع.</p>
          <p>
            رقم الهاتف: 056 420 6066 | 053 134 4107 | البريد الإلكتروني:
            mabdellaziz@experience-mark.com
          </p>
        </div>
      </div>

      <style jsx>{`
        .print-only {
          display: none;
        }
        @media print {
          .main,
          .print-hide {
            display: none;
          }
          .print-only {
            display: block;
            margin: 0;
            padding: 0;
            width: 100%;
            height: 100%;
          }
          .table-container {
            width: 90%;
            max-width: 1000px;
          }
          .table {
            border-collapse: collapse;
            width: 100%;
            border: 1px solid #000;
          }
          .table th,
          .table td {
            border: 1px solid #000;
            padding: 8px;
            text-align: center;
            font-size: 12px;
            color: #000;
          }
          .table th {
            background-color: #fff;
            font-weight: bold;
          }
        }
        .table-responsive {
          overflow-x: auto;
        }
      `}</style>
    </>
  );
}
